import { defineModule, } from 'direct-vuex';
import { ConfirmMoveoutAnonymousResponse, } from '~/api/self-serve-client';
import { AddressData, isAddressData, } from '~/utils/models';
import { isDate, isObject, } from '~/utils/models/isObject';
import { initialiseStoreValue, } from '~/utils/store';

type AddressEntryMode = 'find' | 'manual';
type AddressEntryState = {
	mode: AddressEntryMode,
	address?: AddressData,
};

export interface MoveOutDetails {
	id: string;
	category: string;
	lastReading: number;
	lastReadDate: Date | string;
}

export interface CustomerMoveOutDetails {
	contractId: string;
	propertyAddress: AddressData;
	meter: MoveOutDetails;
}

export interface MoveOutState {
	customerMeterType: string;
	currentStep: 1 | 2 | 3 | 4 | null;
	moveOutDetails: CustomerMoveOutDetails | undefined;
	currentMeterReading: number | null;
	moveOutDate: Date | null;
	addressEntry: AddressEntryState;
	addressList: AddressData[];
	isMoveoutForcefullySucceeded: null | boolean;
	finalBillAmount: number | undefined;
}

const CustomerMoveOutDetailsStorageKey = 'moveOut.details';
const AddressEntryStorageKey = 'moveOut.addressEntry';
const MoveOutDateStorageKey = 'moveOut.date';
const CurrentMeterReadStorageKey = 'moveOut.currentReading';

function isAddressEntry(input: unknown): input is AddressEntryState {
	if (!isObject(input)) {
		return false;
	}

	if (!(input.mode === 'manual' || input.mode === 'find')) {
		return false;
	}

	if (input.address !== undefined && !isAddressData(input.address)) {
		return false;
	}

	return true;
}

function isMeterDetails(input: unknown): input is MoveOutDetails {
	return (
		true &&
		isObject(input) &&
		typeof input.id === 'string' &&
		typeof input.category === 'string' &&
		typeof input.lastReading === 'number' &&
		isDate(input.lastReadDate) &&
		true
	);
}

function isCustomerMoveOutDetails(input: unknown): input is CustomerMoveOutDetails {
	return isObject(input) &&
		typeof input.contractId === 'string' &&
		isAddressData(input.propertyAddress) &&
		isMeterDetails(input.meter);
}

function initialiseCustomerMoveOutDetails() {
	return initialiseStoreValue(CustomerMoveOutDetailsStorageKey,
		isCustomerMoveOutDetails,
		() => undefined,
		(userInput) => {
			// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
			userInput.meter.lastReadDate = new Date(userInput.meter.lastReadDate);
			// eslint-disable-next-line @typescript-eslint/no-unsafe-return
			return userInput;
		});
}

function initialiseAddressEntryState(): AddressEntryState {
	return initialiseStoreValue(AddressEntryStorageKey, isAddressEntry, () => ({
		mode: 'find',
	}));
}

function initialiseMoveOutDate() : Date | null {
	return initialiseStoreValue(MoveOutDateStorageKey, isDate, () => null, (v) => new Date(v));
}

function initialiseCurrentReading() : number | null {
	return initialiseStoreValue(CurrentMeterReadStorageKey, (v): v is number => typeof v === 'string' && !isNaN(parseInt(v, 10)), () => null);
}

export default defineModule({
	namespaced: true as const,
	state: {
		currentStep: 1,
		moveOutDetails: initialiseCustomerMoveOutDetails(),
		currentMeterReading: initialiseCurrentReading(),
		moveOutDate: initialiseMoveOutDate(),
		addressEntry: initialiseAddressEntryState(),
		customerMeterType: null as (string | null),
		addressList: [] as AddressData[],
		isMoveoutForcefullySucceeded: null,
	} as MoveOutState,
	getters: {
		hasExistingMoveOutDetails(state) {
			return state.moveOutDetails !== undefined;
		},
		hasForwardingAddress(state) {
			return isAddressEntry(state.addressEntry);
		},
	},
	mutations: {
		setStep(state, step: MoveOutState['currentStep']) {
			state.currentStep = step;
		},
		updateMeterDetails(state, meterDetails: CustomerMoveOutDetails) {
			sessionStorage.setItem(CustomerMoveOutDetailsStorageKey, JSON.stringify(meterDetails));
			state.moveOutDetails = meterDetails;
		},
		setMeterReading(state, meterReading: number) {
			sessionStorage.setItem(CurrentMeterReadStorageKey, meterReading.toString(10));
			state.currentMeterReading = meterReading;
		},
		checkCustomerMeterType(state, customerMeterType: string) {
			state.customerMeterType = customerMeterType;
		},
		setAddressList(state, list: AddressData[]) {
			state.addressList = list;
		},
		setMoveoutResult(state, { isMoveoutForcefullySucceeded, finalBillAmount, }:ConfirmMoveoutAnonymousResponse) {
			state.isMoveoutForcefullySucceeded = isMoveoutForcefullySucceeded;
			// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
			state.finalBillAmount = finalBillAmount;
		},
		reset(state) {
			sessionStorage.removeItem(CustomerMoveOutDetailsStorageKey);
			sessionStorage.removeItem(AddressEntryStorageKey);
			sessionStorage.removeItem(MoveOutDateStorageKey);
			sessionStorage.removeItem(CurrentMeterReadStorageKey);

			state.moveOutDetails = initialiseCustomerMoveOutDetails();
			state.moveOutDate = initialiseMoveOutDate();
			state.currentMeterReading = null;
			state.addressEntry = initialiseAddressEntryState();

			state.addressList = [];
			state.isMoveoutForcefullySucceeded = null;
		},
		updateMoveOutDate(state, moveOutDate: Date) {
			state.moveOutDate = moveOutDate;
			sessionStorage.setItem(MoveOutDateStorageKey, JSON.stringify(state.moveOutDate));
		},
		setAddressEntryMode(state, addressEntryMode: AddressEntryMode) {
			state.addressEntry = {
				...state.addressEntry,
				mode: addressEntryMode,
			};
			sessionStorage.setItem(AddressEntryStorageKey, JSON.stringify(state.addressEntry));
		},
		updateAddressData(state, address: AddressData) {
			state.addressEntry = {
				...state.addressEntry,
				address,
			};
			sessionStorage.setItem(AddressEntryStorageKey, JSON.stringify(state.addressEntry));
		},
	},
});
